import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Theme, alpha, styled } from "@mui/material/styles";

import { MASCOT_PALETTE } from "@/components/Others/MascotPanel";

import { accentColor, borderColor, headerBg, isDarkTheme } from "../../utils/matchOdds";

/* The match tracker under the hero: the incident timeline and the statistics. */

type Side = "home" | "away";

/** Each side keeps one colour through the tracker: sky / deep ink for home, the crown gold for away. */
export const sideColor = (theme: Theme, side: Side) => {
  const dark = isDarkTheme(theme);
  if (side === "home") return dark ? MASCOT_PALETTE.sky : MASCOT_PALETTE.deepInk;
  return dark ? MASCOT_PALETTE.crown : "#A86B00";
};

export const TrackerBody = styled(Box)({
  padding: "12px 12px 16px",
  "@media (min-width: 900px)": { padding: "14px 20px 20px" },
});

/** The two sides over the timeline and the stats, so a row's side reads without a legend. */
export const TrackerTeams = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  alignItems: "center",
  gap: 12,
  padding: "10px 0",
  marginTop: 12,
  marginBottom: 4,
  borderBottom: `1px solid ${borderColor(theme)}`,
}));

export const SideDot = styled("span", {
  shouldForwardProp: (prop) => prop !== "side",
})<{ side: Side }>(({ theme, side }) => ({
  flexShrink: 0,
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: sideColor(theme, side),
}));

export const TimelineScroll = styled(Box)(({ theme }) => ({
  maxHeight: 420,
  overflowY: "auto",
  overscrollBehavior: "contain",
  borderRadius: 6,
  "&:focus-visible": {
    outline: `2px solid ${isDarkTheme(theme) ? MASCOT_PALETTE.sky : accentColor(theme)}`,
    outlineOffset: 2,
  },
}));

/** An incident's what / who: up to two lines on a narrow phone instead of a one-word ellipsis. */
export const IncidentText = styled(Typography)({
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  overflowWrap: "anywhere",
});

/** A period's heading in the timeline: its name and the score it ended on, between two rules. */
export const PeriodDivider = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
  margin: "12px 0 4px",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
  fontVariantNumeric: "tabular-nums",
  "&::before, &::after": {
    content: '""',
    flex: 1,
    height: 1,
    backgroundColor: borderColor(theme),
  },
}));

/** One incident: the home side's on the left, the minute in the middle, the away side's on the right. */
export const IncidentLine = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 48px 1fr",
  alignItems: "center",
  minHeight: 44,
  padding: "4px 0",
});

export const MinuteBadge = styled("span")(({ theme }) => ({
  justifySelf: "center",
  minWidth: 38,
  padding: "2px 6px",
  borderRadius: 10,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: "18px",
  textAlign: "center",
  fontVariantNumeric: "tabular-nums",
  color: theme.palette.text.primary,
  background: headerBg(theme),
  border: `1px solid ${borderColor(theme)}`,
}));

export const IncidentSide = styled(Box, {
  shouldForwardProp: (prop) => prop !== "side",
})<{ side: Side }>(({ side }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  minWidth: 0,
  flexDirection: side === "home" ? "row-reverse" : "row",
  textAlign: side === "home" ? "right" : "left",
}));

/** The round mark before an incident; a goal's is filled in its side's colour. */
export const IncidentMark = styled("span", {
  shouldForwardProp: (prop) => prop !== "side" && prop !== "scoring",
})<{ side: Side; scoring: boolean }>(({ theme, side, scoring }) => ({
  flexShrink: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 26,
  height: 26,
  borderRadius: "50%",
  color: scoring
    ? theme.palette.getContrastText(sideColor(theme, side))
    : theme.palette.text.secondary,
  backgroundColor: scoring ? sideColor(theme, side) : headerBg(theme),
  border: scoring ? "none" : `1px solid ${borderColor(theme)}`,
}));

/** A booking as the card itself; the second yellow is half yellow, half red. */
export const CardMark = styled("span", {
  shouldForwardProp: (prop) => prop !== "tone",
})<{ tone: "yellow" | "red" | "second-yellow" }>(({ theme, tone }) => {
  const yellow = "#F5C518";
  const red = theme.palette.error.main;
  return {
    display: "block",
    width: 10,
    height: 14,
    borderRadius: 2,
    background:
      tone === "yellow"
        ? yellow
        : tone === "red"
          ? red
          : `linear-gradient(135deg, ${yellow} 50%, ${red} 50%)`,
    boxShadow: `0 1px 2px ${alpha("#000", 0.25)}`,
  };
});

/** The score a goal made, its scorer's figure in their colour. */
export const GoalScore = styled("span")(({ theme }) => ({
  flexShrink: 0,
  padding: "1px 7px",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 800,
  lineHeight: "18px",
  fontVariantNumeric: "tabular-nums",
  background: headerBg(theme),
  border: `1px solid ${borderColor(theme)}`,
  "& b": { fontWeight: 800 },
}));

export const StatRow = styled(Box)({
  display: "grid",
  gridTemplateColumns: "auto 1fr auto",
  alignItems: "center",
  columnGap: 8,
  rowGap: 6,
});

/** A side's figure; the side the row favours gets a pill in its colour. */
export const StatValue = styled("span", {
  shouldForwardProp: (prop) => prop !== "side" && prop !== "leading",
})<{ side: Side; leading: boolean }>(({ theme, side, leading }) => ({
  justifySelf: side === "home" ? "start" : "end",
  minWidth: 32,
  padding: "1px 8px",
  borderRadius: 10,
  fontSize: 13,
  fontWeight: leading ? 800 : 600,
  lineHeight: "20px",
  textAlign: "center",
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
  color: leading
    ? theme.palette.getContrastText(sideColor(theme, side))
    : theme.palette.text.primary,
  backgroundColor: leading ? sideColor(theme, side) : "transparent",
}));

/** Two half-width tracks meeting in the middle: home fills towards the left, away towards the right. */
export const StatBars = styled(Box)({
  gridColumn: "1 / -1",
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 4,
});

export const StatTrack = styled("span", {
  shouldForwardProp: (prop) => prop !== "side",
})<{ side: Side }>(({ theme, side }) => ({
  display: "flex",
  justifyContent: side === "home" ? "flex-end" : "flex-start",
  height: 6,
  borderRadius: 3,
  overflow: "hidden",
  backgroundColor: alpha(theme.palette.text.primary, isDarkTheme(theme) ? 0.08 : 0.06),
}));

export const StatFill = styled("span", {
  shouldForwardProp: (prop) => prop !== "side" && prop !== "leading",
})<{ side: Side; leading: boolean }>(({ theme, side, leading }) => ({
  height: "100%",
  borderRadius: 3,
  backgroundColor: alpha(sideColor(theme, side), leading ? 1 : 0.45),
  transition: "width .4s ease",
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
}));
